import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import FormResult from 'components/Form/FormResult';
import LinkButton from 'components/LinkButton';
import ModalDestructiveConfirm from 'components/ModalDestructiveConfirm';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import {useBackend} from 'state/backend';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import useTeamDeleteGroup from './services';
import {ViewTeamBackendData} from './types';

const updateUrlParamsAndReload = (key: string, value: string | number) => {
    const url = new URL(window.location.href);
    url.searchParams.set(key, String(value));
    setUrl(url.href);
};

const MembersSection = () => {
    const {
        data: {kind, members, membersPagination},
    } = useBackend<ViewTeamBackendData>();

    const {t} = useTranslation('Team.GroupsAndRoles.TeamView', {
        keyPrefix: 'membersSection',
    });

    return (
        <>
            <TableStatic id="members-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>
                            {t('memberName')}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {members.length > 0 &&
                        members.map((member) => (
                            <TableStatic.Row key={member.url}>
                                <TableStatic.Cell>
                                    <a href={member.url}>{member.name}</a>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))}
                    {members.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell>
                                {t(
                                    kind === 'group'
                                        ? 'emptyMembersGroup'
                                        : 'emptyMembersRole',
                                )}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                hideForSinglePage={true}
                id="members-pagination"
                onChange={(page) => updateUrlParamsAndReload('members', page)}
                onFirst={() => updateUrlParamsAndReload('members', 0)}
                onLast={() =>
                    updateUrlParamsAndReload('members', membersPagination.total)
                }
                pageCurrent={membersPagination.page}
                pageStop={membersPagination.total}
            />
        </>
    );
};

const ClientSection = () => {
    const {
        data: {clients, clientsPagination, kind},
    } = useBackend<ViewTeamBackendData>();

    const {t} = useTranslation('Team.GroupsAndRoles.TeamView', {
        keyPrefix: 'clientSection',
    });

    if (kind === 'role') {
        return null;
    }

    return (
        <>
            <SectionHeader
                level={2}
                subtitle={t('subtitleClient')}
                title={t('clients')}
            />
            <TableStatic id="clients-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>
                            {t('clientName')}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {clients && clients.length > 0 ? (
                        clients.map((client) => (
                            <TableStatic.Row key={client.url}>
                                <TableStatic.Cell>
                                    <a href={client.url}>{client.name}</a>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ))
                    ) : (
                        <TableStatic.Row>
                            <TableStatic.Cell>
                                {t('emptyClients')}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            {clientsPagination && (
                <TablePagination
                    hideForSinglePage={true}
                    id="clients-pagination"
                    onChange={(page) =>
                        updateUrlParamsAndReload('clients', page)
                    }
                    onFirst={() => updateUrlParamsAndReload('clients', 1)}
                    onLast={() =>
                        updateUrlParamsAndReload(
                            'clients',
                            clientsPagination.total,
                        )
                    }
                    pageCurrent={clientsPagination.page}
                    pageStop={clientsPagination.total}
                />
            )}
        </>
    );
};

const ApprovalTemplatesSection = () => {
    const {
        data: {approvalTemplates, approvalTemplatesPagination},
    } = useBackend<ViewTeamBackendData>();

    const {t} = useTranslation('Team.GroupsAndRoles.TeamView', {
        keyPrefix: 'approvalTemplatesSection',
    });

    if (approvalTemplates.length === 0) {
        return null;
    }

    return (
        <>
            <SectionHeader
                level={2}
                subtitle={t('approvalTemplatesSubtitle')}
                title={t('approvalTemplates')}
            />
            <TableStatic id="approval-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>
                            {t('approvalTemplateName')}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {approvalTemplates.map((flow) => (
                        <TableStatic.Row key={flow.mid}>
                            <TableStatic.Cell>
                                <a href={flow.url}>{flow.name}</a>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    ))}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                hideForSinglePage={true}
                id="approval-templates-pagination"
                onChange={(page) =>
                    updateUrlParamsAndReload('approvalFlows', page)
                }
                onFirst={() => updateUrlParamsAndReload('approvalFlows', 1)}
                onLast={() =>
                    updateUrlParamsAndReload(
                        'approvalFlows',
                        approvalTemplatesPagination.total,
                    )
                }
                pageCurrent={approvalTemplatesPagination.page}
                pageStop={approvalTemplatesPagination.total}
            />
        </>
    );
};

type NavigationSectionProps = {
    onDeleteClicked: () => void;
};

const NavigationSection: FC<NavigationSectionProps> = ({onDeleteClicked}) => {
    const {
        data: {canDeleteTeam, isAdminTeam, navigationTabs},
    } = useBackend<ViewTeamBackendData>();
    const {t} = useTranslation('Team.GroupsAndRoles.TeamView');

    return (
        <div className="flex items-center justify-between">
            <NavigationTabs.Team
                selection="groupsAndRoles"
                {...navigationTabs}
            />
            {!isAdminTeam && (
                <TooltipWrapper
                    layoutMethod="left"
                    message={canDeleteTeam.failureReason}
                >
                    <Button
                        disabled={!canDeleteTeam.allowed}
                        icon="trash"
                        kind="destructive"
                        onClick={onDeleteClicked}
                        size="sm"
                    >
                        {t('delete')}
                    </Button>
                </TooltipWrapper>
            )}
        </div>
    );
};

export const DeletionModalContent = () => {
    const {t} = useTranslation('Team.GroupsAndRoles.TeamView', {
        keyPrefix: 'deletionModalContent',
    });

    const {
        data: {approvalFlows, kind},
    } = useBackend<ViewTeamBackendData>();

    return (
        <div>
            {approvalFlows.length > 0 && (
                <>
                    <p>{t('approvalFlowsDeleteConfirm')}</p>
                    <ul>
                        {approvalFlows.map((approvalFlow) => (
                            <li key={approvalFlow.mid}>
                                <a
                                    href={approvalFlow.url}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    {approvalFlow.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <p>{t(kind === 'group' ? 'deleteGroup' : 'deleteRole')}</p>
        </div>
    );
};

const TeamView = () => {
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const {
        data: {canEditTeam, isAdminTeam, kind, teamMid, teamName},
    } = useBackend<ViewTeamBackendData>();

    const methods = useForm();

    const {mutateAsync} = useTeamDeleteGroup(kind);

    const {t} = useTranslation('Team.GroupsAndRoles.TeamView');

    const onConfirmDelete = () => {
        mutateAsync({formData: {}, teamMid})
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (response.name === 'team_list_view') {
                            return setUrl(window.Urls.team_list_view());
                        }
                        throw new Error('Unexpected response');
                    },
                }),
            )
            .catch((error) => {
                onApiError(methods)(error);
                setShowDeletionModal(false);
            });
    };

    return (
        <FormProvider {...methods}>
            <div className="space-y-4">
                <DjangoMessages />
                <NavigationSection
                    onDeleteClicked={() => setShowDeletionModal(true)}
                />
                <FormResult />
                <SectionHeader
                    subtitle={
                        isAdminTeam
                            ? t('administratorsTeam')
                            : t(
                                  kind === 'group'
                                      ? 'subtitleGroup'
                                      : 'subtitleRole',
                              )
                    }
                    title={isAdminTeam ? t('administrators') : teamName}
                />
                <MembersSection />
                <ClientSection />
                <ApprovalTemplatesSection />
                <div className="flex">
                    <TooltipWrapper message={canEditTeam.failureReason}>
                        <LinkButton
                            disabled={!canEditTeam.allowed}
                            href={window.Urls.team_edit_view(teamMid)}
                            kind="secondary"
                        >
                            {t('edit')}
                        </LinkButton>
                    </TooltipWrapper>
                </div>
                <ModalDestructiveConfirm
                    header={t(
                        kind === 'group'
                            ? 'deletionModalContent.titleGroup'
                            : 'deletionModalContent.titleRole',
                    )}
                    isOpen={showDeletionModal}
                    onCancel={() => setShowDeletionModal(false)}
                    onConfirm={onConfirmDelete}
                >
                    <DeletionModalContent />
                </ModalDestructiveConfirm>
            </div>
        </FormProvider>
    );
};

export default TeamView;
